import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Video, FileSearch, RefreshCw, Phone, User,
  Heart, Brain, TrendingUp, Calendar, ClipboardList,
  ChevronDown, CheckCircle2, Target, Clock, Activity,
  FileText, Shield, Stethoscope, X, AlertTriangle,
  Send, UserCheck, Barcode, MonitorPlay,
} from 'lucide-react';
import { patients as rawPatients } from '@/data/mockData';

/* ═══ ENRICHED PATIENT ═══ */
const CM_NAMES = ['Dott.ssa L. Conti', 'Dott. M. Ferrara', 'Dott.ssa A. Romano', 'Dott. G. Neri'];
const EROGATORI = ['Coop. Salute Roma', 'Consorzio ADI Lazio', 'Fondazione Gemelli@Home', 'ASL Roma 2'];

function getPatient(id) {
  const p = rawPatients.find(x => x.id === Number(id));
  if (!p) return null;
  return {
    ...p,
    setting: ['Bassa', 'Media', 'Alta'][p.id % 3],
    caseManager: CM_NAMES[p.id % 4],
    erogatore: EROGATORI[p.id % 4],
    paiStatus: 'Attivo',
    paiDaysLeft: 30 + (p.id % 40),
  };
}

/* ═══ MOCK: Valutazioni InterRAI ═══ */
const VALUTAZIONI = [
  { id: 1, date: '10/03/2026', type: 'InterRAI HC (Ammissione)', barthel: 45, cadute: 2, dolore: 'NRS 4/10', cognitivo: 'CPS 2 - Lieve', status: 'Completata' },
  { id: 2, date: '15/01/2026', type: 'InterRAI HC (Rivalutazione)', barthel: 55, cadute: 1, dolore: 'NRS 3/10', cognitivo: 'CPS 2 - Lieve', status: 'Completata' },
  { id: 3, date: '20/11/2025', type: 'InterRAI HC (Ammissione)', barthel: 35, cadute: 3, dolore: 'NRS 6/10', cognitivo: 'CPS 3 - Moderato', status: 'Completata' },
];

/* ═══ MOCK: PAI Data ═══ */
const PAI_VERSIONS = [
  { version: 'v2.0 (Corrente)', date: '10/03/2026', active: true },
  { version: 'v1.1', date: '15/01/2026', active: false },
  { version: 'v1.0', date: '20/11/2025', active: false },
];

const PAI_OBIETTIVI = [
  { id: 1, area: 'Motoria', obiettivo: 'Recupero deambulazione autonoma con ausilio', intervento: 'Fisioterapia 3/sett (45 min)', responsabile: 'Ft. E. Ferrara', stato: 'In corso', progress: 60 },
  { id: 2, area: 'Lesioni', obiettivo: 'Guarigione lesione da decubito sacrale (Stadio II)', intervento: 'Medicazione avanzata 3/sett', responsabile: 'Inf. M. Rossi', stato: 'In corso', progress: 75 },
  { id: 3, area: 'Farmacologica', obiettivo: 'Stabilizzazione terapia anticoagulante', intervento: 'Monitoraggio INR settimanale', responsabile: 'Inf. M. Rossi', stato: 'Raggiunto', progress: 100 },
  { id: 4, area: 'Nutrizionale', obiettivo: 'Miglioramento stato nutrizionale (BMI > 20)', intervento: 'Consulenza dietologica + integratori', responsabile: 'Dott.ssa C. Bianchi', stato: 'In corso', progress: 40 },
];

const PROGRESS_COLORS = {
  'In corso': 'bg-cyan-500',
  'Raggiunto': 'bg-emerald-500',
  'Non raggiunto': 'bg-red-500',
};

const SETTING_COLORS = {
  Bassa: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400',
  Media: 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400',
  Alta: 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-400',
};

/* ═══ TAB LABELS ═══ */
const TABS = [
  { id: 'valutazioni', label: 'Valutazioni e Rischio', icon: Brain },
  { id: 'pai', label: 'PAI Attivo', icon: ClipboardList },
  { id: 'diario', label: 'Diario Clinico', icon: Activity },
  { id: 'esiti', label: 'Esiti (PROMs)', icon: TrendingUp },
  { id: 'documenti', label: 'Documentazione', icon: FileText },
];

/* ═══ MAIN COMPONENT ═══ */
export default function FascicoloClinicoMedico() {
  const { id } = useParams();
  const navigate = useNavigate();
  const patient = getPatient(id);
  const [activeTab, setActiveTab] = useState('valutazioni');
  const [paiVersion, setPaiVersion] = useState('v2.0 (Corrente)');
  const [isRivalutazioneOpen, setIsRivalutazioneOpen] = useState(false);
  const [isTeleconsultoOpen, setIsTeleconsultoOpen] = useState(false);
  const [isCupOpen, setIsCupOpen] = useState(false);
  const [rivalMotivazione, setRivalMotivazione] = useState('');
  const [rivalNote, setRivalNote] = useState('');
  const [cupNre, setCupNre] = useState('');
  const [cupTipo, setCupTipo] = useState('');
  const [cupPriorita, setCupPriorita] = useState('');
  const [cupQuesito, setCupQuesito] = useState('');

  if (!patient) {
    return (
      <AppLayout role="medico">
        <div className="flex items-center justify-center h-96">
          <p className="text-slate-500">Paziente non trovato.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <>
    <AppLayout role="medico">
      <div className="flex flex-col h-full" data-testid="fascicolo-page">
        {/* ══ SCROLLABLE AREA ══ */}
        <div className="flex-1 overflow-y-auto bg-white dark:bg-[#0f172a]">

          {/* ── BACK BUTTON ── */}
          <div className="px-6 pt-4 pb-2 max-w-7xl mx-auto">
            <button onClick={() => navigate('/medico/pazienti')} className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" />
              <span>Archivio Pazienti</span>
            </button>
          </div>

          {/* ══ HEADER CARD (DARK / DETACHED) ══ */}
          <div className="px-6 pb-0 max-w-7xl mx-auto w-full" data-testid="fascicolo-header">
            <div className="rounded-2xl p-6 shadow-sm border border-slate-200 bg-white text-slate-800 dark:bg-slate-900 dark:border-slate-800 dark:text-white dark:shadow-2xl">
              <div className="flex items-start justify-between gap-6">
                {/* Left: Patient Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-3">
                    <h1 className="text-lg font-bold text-slate-800 dark:text-white" data-testid="patient-name">{patient.cognome} {patient.nome}</h1>
                    <Badge className={`${SETTING_COLORS[patient.setting]} border-0 text-[10px] font-bold px-2.5 py-0.5`}>{patient.setting}</Badge>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">{patient.eta} anni · {patient.sesso} · CF: {patient.codiceFiscale}</p>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
                    <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                      <Heart className="w-3.5 h-3.5 text-red-400" />
                      <span className="font-medium">{patient.diagnosiPrincipale?.split('(')[0]?.trim()}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                      <User className="w-3.5 h-3.5" />
                      <span>Caregiver: {patient.caregiver?.nome || 'N/D'} ({patient.caregiver?.relazione})</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                      <Phone className="w-3.5 h-3.5" />
                      <span>{patient.caregiver?.telefono || patient.telefono}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                      <Stethoscope className="w-3.5 h-3.5" />
                      <span>Erogatore: {patient.erogatore}</span>
                    </div>
                  </div>
                </div>

                {/* Right: CTA Buttons */}
                <div className="flex flex-col gap-2 shrink-0" data-testid="cta-row">
                  <button onClick={() => setIsRivalutazioneOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white hover:bg-teal-700 rounded-lg text-xs font-semibold transition-colors border border-transparent shadow-sm" data-testid="cta-rivalutazione">
                    <RefreshCw className="w-3.5 h-3.5" /> Avvia Rivalutazione PAI
                  </button>
                  <button onClick={() => setIsTeleconsultoOpen(true)} className="flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 bg-transparent hover:bg-slate-50 rounded-lg text-xs font-medium transition-colors dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800" data-testid="cta-teleconsulto">
                    <Video className="w-3.5 h-3.5" /> Avvia Teleconsulto
                  </button>
                  <button onClick={() => setIsCupOpen(true)} className="flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 bg-transparent hover:bg-slate-50 rounded-lg text-xs font-medium transition-colors dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800" data-testid="cta-esame">
                    <FileSearch className="w-3.5 h-3.5" /> Richiedi Esame (CUP)
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ══ PILL TABS (DETACHED / CENTERED) ══ */}
          <div className="w-full flex justify-center items-center mt-8 mb-8" data-testid="fascicolo-tabs">
            <div className="flex items-center gap-2 rounded-full p-1 border border-slate-200 bg-slate-100 dark:border-slate-700/50 dark:bg-slate-800/50 overflow-x-auto">
              {TABS.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all
                      ${isActive
                        ? 'bg-white text-cyan-700 shadow-sm dark:bg-slate-700 dark:text-white'
                        : 'text-slate-600 hover:bg-slate-200/50 dark:text-slate-400 dark:hover:bg-slate-700/50'}`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ══ TAB CONTENT ══ */}
          <div className="pb-8">

          {/* ── TAB 1: Valutazioni e Rischio ── */}
          {activeTab === 'valutazioni' && (
            <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="tab-content-valutazioni">
              {/* AI Risk Widget */}
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm" data-testid="ai-risk-widget">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Stratificazione Rischio (AI)</h3>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">Analisi predittiva basata su dati clinici e interRAI</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-red-50 dark:bg-red-950/20 rounded-xl p-4 ring-1 ring-red-200 dark:ring-red-800/30">
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Indice Fragilita</p>
                    <p className="text-3xl font-bold text-red-600 dark:text-red-400">78%</p>
                    <p className="text-[10px] text-red-500 mt-1">Rischio Alto</p>
                  </div>
                  <div className="bg-amber-50 dark:bg-amber-950/20 rounded-xl p-4 ring-1 ring-amber-200 dark:ring-amber-800/30">
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Rischio Cadute</p>
                    <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">Medio</p>
                    <p className="text-[10px] text-amber-500 mt-1">Score: 14/21</p>
                  </div>
                  <div className="bg-emerald-50 dark:bg-emerald-950/20 rounded-xl p-4 ring-1 ring-emerald-200 dark:ring-emerald-800/30">
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Rischio Ospedalizzazione</p>
                    <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">Basso</p>
                    <p className="text-[10px] text-emerald-500 mt-1">Score: 3/15</p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-dashed border-slate-300 dark:border-slate-600">
                  <p className="text-[10px] font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wider mb-1">Explainable AI</p>
                  <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">Rischio alto guidato da: fluttuazione peso (-3kg in 30gg), Barthel Index in declino (55→45), 2 cadute negli ultimi 90gg, comorbidita cardiovascolare attiva. Raccomandazione: rivalutazione nutrizionale e fisioterapica entro 7 giorni.</p>
                </div>
              </div>

              {/* Storico InterRAI */}
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden" data-testid="interrai-history">
                <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
                  <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Storico Valutazioni InterRAI</h3>
                </div>
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-slate-700/60">
                      {['Data', 'Tipo', 'Barthel', 'Cadute (90gg)', 'Dolore', 'Cognitivo', 'Stato'].map(h => (
                        <th key={h} className="px-5 py-2.5 text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {VALUTAZIONI.map(v => (
                      <tr key={v.id} className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50/50 dark:hover:bg-slate-700/20 transition-colors" data-testid={`val-row-${v.id}`}>
                        <td className="px-5 py-3 text-xs font-medium text-slate-700 dark:text-slate-200">{v.date}</td>
                        <td className="px-5 py-3 text-xs text-slate-600 dark:text-slate-300">{v.type}</td>
                        <td className="px-5 py-3">
                          <span className={`text-xs font-bold ${v.barthel < 50 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>{v.barthel}/100</span>
                        </td>
                        <td className="px-5 py-3 text-xs text-slate-600 dark:text-slate-300">{v.cadute}</td>
                        <td className="px-5 py-3 text-xs text-slate-600 dark:text-slate-300">{v.dolore}</td>
                        <td className="px-5 py-3 text-xs text-slate-600 dark:text-slate-300">{v.cognitivo}</td>
                        <td className="px-5 py-3">
                          <Badge className="bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border-0 text-[10px] font-semibold">{v.status}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── TAB 2: PAI Attivo ── */}
          {activeTab === 'pai' && (
            <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="tab-content-pai">
              {/* PAI Version Selector + Timeline */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">PAI Inizio: 10/03/2026</span>
                  </div>
                  <span className="text-slate-300 dark:text-slate-600">|</span>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Scadenza: 10/09/2026</span>
                  </div>
                </div>
                {/* Version Dropdown */}
                <div className="relative" data-testid="pai-version-selector">
                  <select
                    value={paiVersion}
                    onChange={e => setPaiVersion(e.target.value)}
                    className="h-9 px-3 pr-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-medium text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 appearance-none"
                  >
                    {PAI_VERSIONS.map(v => (
                      <option key={v.version} value={v.version}>{v.version} — {v.date}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                </div>
              </div>

              {/* Obiettivi e Interventi */}
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden" data-testid="pai-obiettivi">
                <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Obiettivi e Interventi Pianificati</h3>
                  <Badge className="bg-cyan-50 dark:bg-cyan-950/30 text-cyan-700 dark:text-cyan-400 border-0 text-[10px] font-semibold">{PAI_OBIETTIVI.length} obiettivi</Badge>
                </div>
                <div className="flex flex-col gap-0">
                  {PAI_OBIETTIVI.map(obj => {
                    const barColor = PROGRESS_COLORS[obj.stato] || 'bg-slate-400';
                    return (
                      <div key={obj.id} className="px-5 py-4 border-b border-slate-100 dark:border-slate-700/50 last:border-b-0" data-testid={`obiettivo-${obj.id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-0 text-[9px] font-bold px-2">{obj.area}</Badge>
                              <h4 className="text-sm font-medium text-slate-800 dark:text-slate-100">{obj.obiettivo}</h4>
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">{obj.intervento} · {obj.responsabile}</p>
                          </div>
                          <Badge className={`border-0 text-[10px] font-semibold shrink-0 ml-4
                            ${obj.stato === 'Raggiunto' ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400' :
                              obj.stato === 'In corso' ? 'bg-cyan-50 dark:bg-cyan-950/30 text-cyan-700 dark:text-cyan-400' :
                              'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400'}`}>
                            {obj.stato === 'Raggiunto' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                            {obj.stato}
                          </Badge>
                        </div>
                        {/* Progress Bar */}
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${obj.progress}%` }} />
                          </div>
                          <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 w-8 text-right">{obj.progress}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* PAI Summary Cards */}
              <div className="grid grid-cols-3 gap-4" data-testid="pai-summary">
                {[
                  { label: 'Accessi Settimanali', value: '5', sub: 'Inf. 3 + Ft. 2', icon: Target, color: 'text-cyan-600 dark:text-cyan-400', bg: 'bg-cyan-50 dark:bg-cyan-950/30' },
                  { label: 'Prossima Rivalutazione', value: '15/06', sub: 'Tra 63 giorni', icon: RefreshCw, color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-950/30' },
                  { label: 'Completamento Obiettivi', value: '1/4', sub: '25% raggiunto', icon: Shield, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/30' },
                ].map(card => (
                  <div key={card.label} className={`${card.bg} rounded-xl p-4 ring-1 ring-slate-200/50 dark:ring-slate-700/30`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{card.label}</span>
                      <card.icon className={`w-4 h-4 ${card.color}`} />
                    </div>
                    <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{card.sub}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── TAB 3-5: Placeholder ── */}
          {['diario', 'esiti', 'documenti'].includes(activeTab) && (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-7 h-7 text-slate-400" />
                </div>
                <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-1">
                  {activeTab === 'diario' ? 'Diario Clinico e Telemonitoraggio' : activeTab === 'esiti' ? 'Esiti e Soddisfazione (PROMs/PREMs)' : 'Documentazione e FSE'}
                </p>
                <p className="text-xs text-slate-400">Modulo in fase di sviluppo — Fase 2</p>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </AppLayout>

    {/* ══════════════════════════════════════════════════ */}
    {/* MODALE 1: AVVIA RIVALUTAZIONE PAI                 */}
    {/* ══════════════════════════════════════════════════ */}
    {isRivalutazioneOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="modal-rivalutazione">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsRivalutazioneOpen(false)} />
        <div className="relative w-full max-w-3xl mx-4 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                <RefreshCw className="w-4.5 h-4.5 text-teal-600 dark:text-teal-400" />
              </div>
              <h2 className="text-base font-bold text-slate-800 dark:text-white">Avvia Rivalutazione PAI</h2>
            </div>
            <button onClick={() => setIsRivalutazioneOpen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="modal-rivalutazione-close">
              <X className="w-4 h-4" />
            </button>
          </div>
          {/* Body */}
          <div className="p-6 flex flex-col gap-5">
            <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Attenzione</p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5 leading-relaxed">L'avvio di una rivalutazione archivierà il PAI corrente (v2.0) al termine del processo. Tutti i dati del piano attuale saranno conservati nello storico versioni.</p>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Motivazione Rivalutazione</label>
              <select value={rivalMotivazione} onChange={e => setRivalMotivazione(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/30" data-testid="rival-motivazione-select">
                <option value="">Seleziona motivazione...</option>
                <option value="scadenza">Scadenza Naturale</option>
                <option value="aggravamento">Aggravamento Clinico</option>
                <option value="obiettivi">Raggiungimento Obiettivi</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Note Cliniche Preliminari <span className="font-normal text-slate-400">(Visibili all'équipe)</span></label>
              <textarea value={rivalNote} onChange={e => setRivalNote(e.target.value)} rows={4} placeholder="Inserisci note cliniche a supporto della rivalutazione..." className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 resize-none" data-testid="rival-note-textarea" />
            </div>
          </div>
          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-800">
            <button onClick={() => setIsRivalutazioneOpen(false)} className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" data-testid="rival-annulla-btn">Annulla</button>
            <button onClick={() => { setIsRivalutazioneOpen(false); navigate(`/medico/rivalutazione-clinica/${id}`); }} className="px-5 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold transition-colors shadow-sm" data-testid="rival-procedi-btn">Procedi al Wizard di Valutazione</button>
          </div>
        </div>
      </div>
    )}

    {/* ══════════════════════════════════════════════════ */}
    {/* MODALE 2: AVVIA TELECONSULTO                      */}
    {/* ══════════════════════════════════════════════════ */}
    {isTeleconsultoOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="modal-teleconsulto">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsTeleconsultoOpen(false)} />
        <div className="relative w-full max-w-2xl mx-4 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
          {/* Header scuro */}
          <div className="flex items-center justify-between px-5 py-4 bg-slate-800 dark:bg-slate-950">
            <div className="flex items-center gap-3">
              <MonitorPlay className="w-5 h-5 text-emerald-400" />
              <h2 className="text-sm font-bold text-white">Stanza Teleconsulto: {patient.cognome} {patient.nome}</h2>
            </div>
            <button onClick={() => setIsTeleconsultoOpen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-700 transition-colors" data-testid="modal-teleconsulto-close">
              <X className="w-4 h-4" />
            </button>
          </div>
          {/* Body */}
          <div className="p-6 flex flex-col gap-5">
            {/* Video mockup */}
            <div className="h-64 bg-slate-950 dark:bg-black rounded-xl flex items-center justify-center" data-testid="teleconsulto-video-area">
              <div className="text-center">
                <Video className="w-12 h-12 text-slate-600 mx-auto mb-2" />
                <p className="text-xs text-slate-500">In attesa di connessione...</p>
              </div>
            </div>
            {/* Partecipanti */}
            <div>
              <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-3">Partecipanti alla sessione</p>
              <div className="flex flex-col gap-2.5">
                {[
                  { label: `${patient.caregiver?.nome || 'Caregiver'} (Paziente/Caregiver)`, checked: true },
                  { label: `${patient.caseManager} (Case Manager)`, checked: true },
                  { label: 'Dott. F. Marchetti (Medico MMG)', checked: false },
                ].map((p, i) => (
                  <label key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors" data-testid={`partecipante-${i}`}>
                    <input type="checkbox" defaultChecked={p.checked} className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-teal-600 focus:ring-teal-500/30" />
                    <UserCheck className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-700 dark:text-slate-200">{p.label}</span>
                  </label>
                ))}
              </div>
            </div>
            {/* Invito */}
            <div>
              <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Invia link invito via SMS/Email</p>
              <div className="flex gap-2">
                <input type="text" placeholder="+39 xxx xxx xxxx o email@..." className="flex-1 h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30" data-testid="teleconsulto-invite-input" />
                <button className="px-4 h-10 rounded-lg border border-slate-300 dark:border-slate-600 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-1.5" data-testid="teleconsulto-send-link">
                  <Send className="w-3.5 h-3.5" /> Invia Link
                </button>
              </div>
            </div>
          </div>
          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800">
            <button className="w-full py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2 shadow-sm" data-testid="teleconsulto-entra-btn">
              <Video className="w-4 h-4" /> Entra nella Stanza
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ══════════════════════════════════════════════════ */}
    {/* DRAWER 3: RICHIEDI ESAME (CUP)                    */}
    {/* ══════════════════════════════════════════════════ */}
    {isCupOpen && (
      <div className="fixed inset-0 z-50" data-testid="drawer-cup">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setIsCupOpen(false)} />
        <div className="absolute top-0 right-0 h-full w-full max-w-[500px] bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-800 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <FileSearch className="w-4.5 h-4.5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-slate-800 dark:text-white">Richiesta Prestazione</h2>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">Integrazione CUP Regionale</p>
              </div>
            </div>
            <button onClick={() => setIsCupOpen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="drawer-cup-close">
              <X className="w-4 h-4" />
            </button>
          </div>
          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-5">
            {/* NRE */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Numero Ricetta Elettronica (NRE)</label>
              <div className="relative">
                <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="text" value={cupNre} onChange={e => setCupNre(e.target.value)} placeholder="0A0 0000 0000 00000" className="w-full h-10 pl-10 pr-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 font-mono" data-testid="cup-nre-input" />
              </div>
            </div>
            {/* Tipologia */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Tipologia Esame</label>
              <select value={cupTipo} onChange={e => setCupTipo(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30" data-testid="cup-tipo-select">
                <option value="">Seleziona tipologia...</option>
                <option value="specialistica">Visita Specialistica</option>
                <option value="ematochimici">Esami Ematochimici</option>
                <option value="diagnostica">Diagnostica per Immagini</option>
                <option value="ecografia">Ecografia</option>
                <option value="fisioterapia">Ciclo Fisioterapico</option>
              </select>
            </div>
            {/* Priorità RAO */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">Priorità (RAO)</label>
              <div className="flex gap-2" data-testid="cup-priorita-group">
                {[
                  { code: 'U', label: '72h', color: 'red' },
                  { code: 'B', label: '10gg', color: 'amber' },
                  { code: 'D', label: '30gg', color: 'sky' },
                  { code: 'P', label: '120gg', color: 'slate' },
                ].map(p => {
                  const sel = cupPriorita === p.code;
                  const colorMap = { red: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 ring-red-300 dark:ring-red-700', amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 ring-amber-300 dark:ring-amber-700', sky: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 ring-sky-300 dark:ring-sky-700', slate: 'bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 ring-slate-300 dark:ring-slate-600' };
                  return (
                    <button key={p.code} onClick={() => setCupPriorita(p.code)} className={`flex-1 py-2.5 rounded-lg text-center text-xs font-bold transition-all ${sel ? `${colorMap[p.color]} ring-2` : 'bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'}`} data-testid={`cup-prio-${p.code}`}>
                      <span className="text-base">{p.code}</span>
                      <span className="block text-[10px] font-medium mt-0.5">{p.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Quesito Diagnostico */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Quesito Diagnostico</label>
              <textarea value={cupQuesito} onChange={e => setCupQuesito(e.target.value)} rows={4} placeholder="Descrivere il quesito clinico e la motivazione della richiesta..." className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-none" data-testid="cup-quesito-textarea" />
            </div>
          </div>
          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 shrink-0">
            <button className="w-full py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2 shadow-sm" data-testid="cup-inoltra-btn">
              <Send className="w-4 h-4" /> Inoltra Richiesta al CUP
            </button>
          </div>
        </div>
      </div>
    )}

    </>
  );
}
